import { v7 as uuidv7 } from 'uuid';
import type { SyncStore } from './store.js';

// Stored in the existing meta table: no IndexedDB/SQLite schema upgrade needed.
// All cooperating tabs/processes serialize uploads and reconciliation here.
const KEY = 'outbox.lease';
const TTL = 30_000;
type Lease = { owner: string; until: number };
function parse(raw: string | undefined): Lease | null {
  try {
    return raw ? (JSON.parse(raw) as Lease) : null;
  } catch {
    return null;
  }
}

export async function withSyncLease<T>(
  store: SyncStore,
  work: (signal: AbortSignal, assertOwned: (tx: SyncStore) => Promise<void>) => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  const owner = uuidv7();
  const aborter = new AbortController();
  const abort = () => aborter.abort();
  signal?.addEventListener('abort', abort, { once: true });
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  try {
    while (true) {
      if (signal?.aborted) abort();
      aborter.signal.throwIfAborted();
      const acquired = await store.transaction(async (tx) => {
        const lease = parse(await tx.getMeta(KEY));
        if (lease && lease.until > Date.now()) return false;
        await tx.setMeta(KEY, JSON.stringify({ owner, until: Date.now() + TTL }));
        return true;
      });
      if (acquired) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    heartbeat = setInterval(() => {
      void store
        .transaction(async (tx) => {
          const lease = parse(await tx.getMeta(KEY));
          if (lease?.owner !== owner) {
            abort();
            return;
          }
          await tx.setMeta(KEY, JSON.stringify({ owner, until: Date.now() + TTL }));
        })
        .catch(abort);
    }, 5_000);
    return await work(aborter.signal, async (tx) => {
      aborter.signal.throwIfAborted();
      if (parse(await tx.getMeta(KEY))?.owner !== owner) throw new Error('sync_lease_lost');
    });
  } finally {
    if (heartbeat) clearInterval(heartbeat);
    signal?.removeEventListener('abort', abort);
    await store.transaction(async (tx) => {
      if (parse(await tx.getMeta(KEY))?.owner === owner) await tx.setMeta(KEY, '');
    });
  }
}
